import { fetchManagementJson, managementTenantKey } from "@/lib/management-api";

export type CatalogNotificationChannel = {
  notificationChannelId: number;
  tenantKey: string;
  channelCode: string;
  displayName: string;
  isEnabled: boolean;
  configJson?: string | null;
};

export type CatalogTierChannel = {
  escalationTierChannelId: number;
  notificationChannelId: number;
  channelCode: string;
  sortOrder: number;
  isRequired: boolean;
  templateKey?: string | null;
  destinationRuleJson?: string | null;
};

export type CatalogTier = {
  escalationTierId: number;
  tierOrder: number;
  roleCode: string;
  timeoutSeconds: number;
  displayName: string;
  channels: CatalogTierChannel[];
};

export type CatalogPolicy = {
  escalationPolicyId: number;
  tenantKey: string;
  code: string;
  displayName: string;
  isActive: boolean;
  tiers: CatalogTier[];
};

export type CatalogRequestType = {
  requestTypeId: number;
  tenantKey: string;
  code: string;
  title: string;
  confirmationTemplate: string;
  isPatientSelectable: boolean;
  isEnabled: boolean;
  sortOrder: number;
  priority: string;
  escalationPolicyId?: number | null;
  iconKey?: string | null;
};

export type CatalogConfiguration = {
  configurationId: number;
  wakePhrase: string;
  wakeWordOnnxPath?: string | null;
  voiceAgentId?: string | null;
  hasWebhookSecret: boolean;
  defaultEscalationPolicyId?: number | null;
  isEnabled: boolean;
};

export type CatalogVoiceAgentBinding = {
  voiceAgentBindingId: number;
  tenantKey: string;
  deviceId?: string | null;
  organizationId?: number | null;
  organizationSiteId?: number | null;
  organizationSiteLocationId?: number | null;
  voiceAgentId: string;
  isActive: boolean;
  organizationName?: string | null;
  siteName?: string | null;
  locationName?: string | null;
  scope: "device" | "tenant" | "org" | "site" | "location" | string;
};

export type LocationFilterOption = {
  id: number;
  name: string;
  parentId?: number | null;
};

export type CatalogPriorityOption = {
  label: string;
  value: string;
};

export type CatalogSnapshot = {
  tenantKey: string;
  configuration: CatalogConfiguration | null;
  notificationChannels: CatalogNotificationChannel[];
  policies: CatalogPolicy[];
  requestTypes: CatalogRequestType[];
  voiceAgentBindings: CatalogVoiceAgentBinding[];
  priorityOptions: CatalogPriorityOption[];
};

function pick<T>(
  obj: Record<string, unknown>,
  a: string,
  b: string,
): T | undefined {
  return (obj[a] ?? obj[b]) as T | undefined;
}

/** Prefer backend error/success message fields; fall back to message (Pascal or camel). */
function apiMessage(
  body: Record<string, unknown>,
  fallback = "",
): string {
  const value =
    body.errorMessage ??
    body.ErrorMessage ??
    body.successMessage ??
    body.SuccessMessage ??
    body.message ??
    body.Message ??
    fallback;
  return String(value ?? fallback);
}

function mapChannel(raw: Record<string, unknown>): CatalogNotificationChannel {
  return {
    notificationChannelId: Number(
      pick(raw, "notificationChannelId", "NotificationChannelId") ?? 0,
    ),
    tenantKey: String(pick(raw, "tenantKey", "TenantKey") ?? ""),
    channelCode: String(pick(raw, "channelCode", "ChannelCode") ?? ""),
    displayName: String(pick(raw, "displayName", "DisplayName") ?? ""),
    isEnabled: Boolean(pick(raw, "isEnabled", "IsEnabled") ?? true),
    configJson:
      (pick(raw, "configJson", "ConfigJson") as string | null) ?? null,
  };
}

function mapTierChannel(raw: Record<string, unknown>): CatalogTierChannel {
  return {
    escalationTierChannelId: Number(
      pick(raw, "escalationTierChannelId", "EscalationTierChannelId") ?? 0,
    ),
    notificationChannelId: Number(
      pick(raw, "notificationChannelId", "NotificationChannelId") ?? 0,
    ),
    channelCode: String(pick(raw, "channelCode", "ChannelCode") ?? ""),
    sortOrder: Number(pick(raw, "sortOrder", "SortOrder") ?? 0),
    isRequired: Boolean(pick(raw, "isRequired", "IsRequired") ?? true),
    templateKey:
      (pick(raw, "templateKey", "TemplateKey") as string | null) ?? null,
    destinationRuleJson:
      (pick(raw, "destinationRuleJson", "DestinationRuleJson") as
        | string
        | null) ?? null,
  };
}

function mapTier(raw: Record<string, unknown>): CatalogTier {
  const channelsRaw =
    (pick(raw, "channels", "Channels") as
      | Record<string, unknown>[]
      | undefined) ?? [];
  return {
    escalationTierId: Number(
      pick(raw, "escalationTierId", "EscalationTierId") ?? 0,
    ),
    tierOrder: Number(pick(raw, "tierOrder", "TierOrder") ?? 0),
    roleCode: String(pick(raw, "roleCode", "RoleCode") ?? ""),
    timeoutSeconds: Number(pick(raw, "timeoutSeconds", "TimeoutSeconds") ?? 0),
    displayName: String(pick(raw, "displayName", "DisplayName") ?? ""),
    channels: channelsRaw.map(mapTierChannel),
  };
}

function mapPolicy(raw: Record<string, unknown>): CatalogPolicy {
  const tiersRaw =
    (pick(raw, "tiers", "Tiers") as Record<string, unknown>[] | undefined) ??
    [];
  return {
    escalationPolicyId: Number(
      pick(raw, "escalationPolicyId", "EscalationPolicyId") ?? 0,
    ),
    tenantKey: String(pick(raw, "tenantKey", "TenantKey") ?? ""),
    code: String(pick(raw, "code", "Code") ?? ""),
    displayName: String(pick(raw, "displayName", "DisplayName") ?? ""),
    isActive: Boolean(pick(raw, "isActive", "IsActive") ?? true),
    tiers: tiersRaw.map(mapTier),
  };
}

function mapRequestType(raw: Record<string, unknown>): CatalogRequestType {
  const policyId = pick(raw, "escalationPolicyId", "EscalationPolicyId");
  return {
    requestTypeId: Number(pick(raw, "requestTypeId", "RequestTypeId") ?? 0),
    tenantKey: String(pick(raw, "tenantKey", "TenantKey") ?? ""),
    code: String(pick(raw, "code", "Code") ?? ""),
    title: String(pick(raw, "title", "Title") ?? ""),
    confirmationTemplate: String(
      pick(raw, "confirmationTemplate", "ConfirmationTemplate") ?? "",
    ),
    isPatientSelectable: Boolean(
      pick(raw, "isPatientSelectable", "IsPatientSelectable") ?? true,
    ),
    isEnabled: Boolean(pick(raw, "isEnabled", "IsEnabled") ?? true),
    sortOrder: Number(pick(raw, "sortOrder", "SortOrder") ?? 0),
    escalationPolicyId:
      policyId == null || policyId === "" ? null : Number(policyId),
    iconKey: (pick(raw, "iconKey", "IconKey") as string | null) ?? null,
    priority: String(pick(raw, "priority", "Priority") ?? ""),
  };
}

function mapPriorityOptions(
  raw: Record<string, unknown>,
): CatalogPriorityOption {
  return {
    label: String(pick(raw, "label", "Label")),
    value: String(pick(raw, "value", "Value")),
  };
}

function mapConfiguration(
  raw: Record<string, unknown> | null | undefined,
): CatalogConfiguration | null {
  if (!raw) return null;
  const defaultId = pick(
    raw,
    "defaultEscalationPolicyId",
    "DefaultEscalationPolicyId",
  );
  return {
    configurationId: Number(
      pick(raw, "configurationId", "ConfigurationId") ?? 0,
    ),
    wakePhrase: String(pick(raw, "wakePhrase", "WakePhrase") ?? ""),
    wakeWordOnnxPath:
      (pick(raw, "wakeWordOnnxPath", "WakeWordOnnxPath") as string | null) ??
      null,
    voiceAgentId:
      (pick(raw, "voiceAgentId", "VoiceAgentId") as string | null) ?? null,
    hasWebhookSecret: Boolean(
      pick(raw, "hasWebhookSecret", "HasWebhookSecret") ?? false,
    ),
    defaultEscalationPolicyId:
      defaultId == null || defaultId === "" ? null : Number(defaultId),
    isEnabled: Boolean(pick(raw, "isEnabled", "IsEnabled") ?? true),
  };
}

function mapVoiceBinding(
  raw: Record<string, unknown>,
): CatalogVoiceAgentBinding {
  const siteId = pick(raw, "organizationSiteId", "OrganizationSiteId");
  const locationId = pick(
    raw,
    "organizationSiteLocationId",
    "OrganizationSiteLocationId",
  );
  const orgId = pick(raw, "organizationId", "OrganizationId");
  const deviceId = pick(raw, "deviceId", "DeviceId");
  return {
    voiceAgentBindingId: Number(
      pick(raw, "voiceAgentBindingId", "VoiceAgentBindingId") ?? 0,
    ),
    tenantKey: String(pick(raw, "tenantKey", "TenantKey") ?? ""),
    deviceId: deviceId == null || deviceId === "" ? null : String(deviceId),
    organizationId: orgId == null || orgId === "" ? null : Number(orgId),
    organizationSiteId: siteId == null || siteId === "" ? null : Number(siteId),
    organizationSiteLocationId:
      locationId == null || locationId === "" ? null : Number(locationId),
    voiceAgentId: String(pick(raw, "voiceAgentId", "VoiceAgentId") ?? ""),
    isActive: Boolean(pick(raw, "isActive", "IsActive") ?? true),
    organizationName:
      (pick(raw, "organizationName", "OrganizationName") as string | null) ??
      null,
    siteName: (pick(raw, "siteName", "SiteName") as string | null) ?? null,
    locationName:
      (pick(raw, "locationName", "LocationName") as string | null) ?? null,
    scope: String(pick(raw, "scope", "Scope") ?? "tenant"),
  };
}

async function readJson(res: Response): Promise<Record<string, unknown>> {
  try {
    return (await res.json()) as Record<string, unknown>;
  } catch {
    return {};
  }
}

function tenantQs(): string {
  return `tenantKey=${encodeURIComponent(managementTenantKey())}`;
}

export async function fetchCatalog(): Promise<{
  ok: boolean;
  unavailable?: boolean;
  catalog?: CatalogSnapshot;
  message?: string;
}> {
  if (!managementTenantKey()) {
    return {
      ok: false,
      unavailable: true,
      message: "tenantKey is required.",
    };
  }
  const res = await fetchManagementJson(`catalog?${tenantQs()}`);
  const body = await readJson(res);
  const ok = res.ok && Boolean(body.isSuccess ?? body.IsSuccess ?? true);
  if (!ok) {
    return {
      ok: false,
      unavailable: res.status === 404 || res.status === 400,
      message: apiMessage(body, "Failed to load catalog"),
    };
  }

  const channels =
    (pick(body, "notificationChannels", "NotificationChannels") as
      | Record<string, unknown>[]
      | undefined) ?? [];
  const policies =
    (pick(body, "policies", "Policies") as
      | Record<string, unknown>[]
      | undefined) ?? [];
  const requestTypes =
    (pick(body, "requestTypes", "RequestTypes") as
      | Record<string, unknown>[]
      | undefined) ?? [];
  const bindings =
    (pick(body, "voiceAgentBindings", "VoiceAgentBindings") as
      | Record<string, unknown>[]
      | undefined) ?? [];
  const configRaw = pick(body, "configuration", "Configuration") as
    | Record<string, unknown>
    | null
    | undefined;
  const priorityOptions =
    (pick(body, "priorityOptions", "PriorityOptions") as
      | Record<string, unknown>[]
      | undefined) ?? [];

  return {
    ok: true,
    catalog: {
      tenantKey: String(
        pick(body, "tenantKey", "TenantKey") ?? managementTenantKey(),
      ),
      configuration: mapConfiguration(configRaw),
      notificationChannels: channels.map(mapChannel),
      policies: policies.map(mapPolicy),
      requestTypes: requestTypes.map(mapRequestType),
      voiceAgentBindings: bindings.map(mapVoiceBinding),
      priorityOptions: priorityOptions.map(mapPriorityOptions),
    },
  };
}

export async function saveConfiguration(payload: {
  wakePhrase: string;
  wakeWordOnnxPath?: string | null;
  voiceAgentId?: string | null;
  voiceAgentWebhookSecret?: string | null;
  updateWebhookSecret: boolean;
  defaultEscalationPolicyId?: number | null;
  isEnabled: boolean;
}): Promise<{
  ok: boolean;
  message?: string;
  configuration?: CatalogConfiguration;
}> {
  const res = await fetchManagementJson(`configuration?${tenantQs()}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const body = await readJson(res);
  const ok = res.ok && Boolean(body.isSuccess ?? body.IsSuccess);
  const configRaw = pick(body, "configuration", "Configuration") as
    | Record<string, unknown>
    | undefined;
  return {
    ok,
    message: apiMessage(body, ok ? "" : "Save failed"),
    configuration: mapConfiguration(configRaw) ?? undefined,
  };
}

export async function saveNotificationChannel(
  payload: {
    channelCode: string;
    displayName: string;
    isEnabled: boolean;
    configJson?: string | null;
  },
  id?: number,
): Promise<{
  ok: boolean;
  message?: string;
  channel?: CatalogNotificationChannel;
}> {
  const path =
    id && id > 0
      ? `notification-channels/${id}?${tenantQs()}`
      : `notification-channels?${tenantQs()}`;
  const res = await fetchManagementJson(path, {
    method: id && id > 0 ? "PUT" : "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const body = await readJson(res);
  const ok = res.ok && Boolean(body.isSuccess ?? body.IsSuccess);
  const channelRaw = pick(body, "channel", "Channel") as
    | Record<string, unknown>
    | undefined;
  return {
    ok,
    message: apiMessage(body, ok ? "" : "Save failed"),
    channel: channelRaw ? mapChannel(channelRaw) : undefined,
  };
}

export async function savePolicy(
  payload: {
    code: string;
    displayName: string;
    isActive: boolean;
    tiers: Array<{
      escalationTierId?: number | null;
      tierOrder: number;
      roleCode: string;
      timeoutSeconds: number;
      displayName: string;
      channels: Array<{
        escalationTierChannelId?: number | null;
        notificationChannelId: number;
        sortOrder: number;
        isRequired: boolean;
        templateKey?: string | null;
        destinationRuleJson?: string | null;
      }>;
    }>;
  },
  id?: number,
): Promise<{ ok: boolean; message?: string; policy?: CatalogPolicy }> {
  const path =
    id && id > 0 ? `policies/${id}?${tenantQs()}` : `policies?${tenantQs()}`;
  const res = await fetchManagementJson(path, {
    method: id && id > 0 ? "PUT" : "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const body = await readJson(res);
  const ok = res.ok && Boolean(body.isSuccess ?? body.IsSuccess);
  const policyRaw = pick(body, "policy", "Policy") as
    | Record<string, unknown>
    | undefined;
  return {
    ok,
    message: apiMessage(body, ok ? "" : "Save failed"),
    policy: policyRaw ? mapPolicy(policyRaw) : undefined,
  };
}

export async function setPolicyActive(
  policyId: number,
  isActive: boolean,
): Promise<{ ok: boolean; message?: string }> {
  const res = await fetchManagementJson(
    `policies/${policyId}/active?${tenantQs()}`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ isActive }),
    },
  );
  const body = await readJson(res);
  return {
    ok: res.ok && Boolean(body.isSuccess ?? body.IsSuccess),
    message: apiMessage(body),
  };
}

export async function saveRequestType(
  payload: {
    code: string;
    title: string;
    confirmationTemplate: string;
    isPatientSelectable: boolean;
    isEnabled: boolean;
    sortOrder: number;
    escalationPolicyId?: number | null;
    iconKey?: string | null;
    priority?: string | null;
  },
  id?: number,
): Promise<{
  ok: boolean;
  message?: string;
  requestType?: CatalogRequestType;
}> {
  const path =
    id && id > 0
      ? `request-types/${id}?${tenantQs()}`
      : `request-types?${tenantQs()}`;
  const res = await fetchManagementJson(path, {
    method: id && id > 0 ? "PUT" : "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const body = await readJson(res);
  const ok = res.ok && Boolean(body.isSuccess ?? body.IsSuccess);
  const rtRaw = pick(body, "requestType", "RequestType") as
    | Record<string, unknown>
    | undefined;
  return {
    ok,
    message: apiMessage(body, ok ? "" : "Save failed"),
    requestType: rtRaw ? mapRequestType(rtRaw) : undefined,
  };
}

export async function fetchLocationFilters(options?: {
  organizationId?: number | null;
  organizationSiteId?: number | null;
}): Promise<{
  ok: boolean;
  organizations: LocationFilterOption[];
  sites: LocationFilterOption[];
  locations: LocationFilterOption[];
  message?: string;
}> {
  const params = new URLSearchParams();
  params.set("tenantKey", managementTenantKey());
  if (options?.organizationId) {
    params.set("organizationId", String(options.organizationId));
  }
  if (options?.organizationSiteId) {
    params.set("organizationSiteId", String(options.organizationSiteId));
  }
  const res = await fetchManagementJson(`location-filters?${params}`);
  const body = await readJson(res);
  const ok = res.ok && Boolean(body.isSuccess ?? body.IsSuccess ?? true);
  const mapOpt = (raw: Record<string, unknown>): LocationFilterOption => {
    const parent = pick(raw, "parentId", "ParentId");
    return {
      id: Number(pick(raw, "id", "Id") ?? 0),
      name: String(pick(raw, "name", "Name") ?? ""),
      parentId: parent == null || parent === "" ? null : Number(parent),
    };
  };
  const orgs =
    (pick(body, "organizations", "Organizations") as
      | Record<string, unknown>[]
      | undefined) ?? [];
  const sites =
    (pick(body, "sites", "Sites") as Record<string, unknown>[] | undefined) ??
    [];
  const locations =
    (pick(body, "locations", "Locations") as
      | Record<string, unknown>[]
      | undefined) ?? [];
  return {
    ok,
    message: ok
      ? undefined
      : apiMessage(body, "Failed to load locations"),
    organizations: orgs.map(mapOpt),
    sites: sites.map(mapOpt),
    locations: locations.map(mapOpt),
  };
}

export async function saveVoiceAgentBinding(
  payload: {
    deviceId?: string | null;
    organizationId?: number | null;
    organizationSiteId?: number | null;
    organizationSiteLocationId?: number | null;
    voiceAgentId: string;
    isActive: boolean;
  },
  id?: number,
): Promise<{
  ok: boolean;
  message?: string;
  binding?: CatalogVoiceAgentBinding;
}> {
  const path =
    id && id > 0
      ? `voice-agent-bindings/${id}?${tenantQs()}`
      : `voice-agent-bindings?${tenantQs()}`;
  const res = await fetchManagementJson(path, {
    method: id && id > 0 ? "PUT" : "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const body = await readJson(res);
  const ok = res.ok && Boolean(body.isSuccess ?? body.IsSuccess);
  const bindingRaw = pick(body, "binding", "Binding") as
    | Record<string, unknown>
    | undefined;
  return {
    ok,
    message: apiMessage(body, ok ? "" : "Save failed"),
    binding: bindingRaw ? mapVoiceBinding(bindingRaw) : undefined,
  };
}

export async function deleteVoiceAgentBinding(
  id: number,
): Promise<{ ok: boolean; message?: string }> {
  const res = await fetchManagementJson(
    `voice-agent-bindings/${id}?${tenantQs()}`,
    { method: "DELETE" },
  );
  const body = await readJson(res);
  return {
    ok: res.ok && Boolean(body.isSuccess ?? body.IsSuccess),
    message: apiMessage(body),
  };
}

export function parseRecipients(destinationRuleJson?: string | null): string[] {
  if (!destinationRuleJson?.trim()) return [];
  try {
    const parsed = JSON.parse(destinationRuleJson) as { recipients?: unknown };
    if (!Array.isArray(parsed.recipients)) return [];
    return parsed.recipients.map(String).filter(Boolean);
  } catch {
    return [];
  }
}

export function recipientsToJson(recipients: string[]): string | null {
  const cleaned = recipients.map((r) => r.trim()).filter(Boolean);
  if (cleaned.length === 0) return null;
  return JSON.stringify({ recipients: cleaned });
}

export type RecipientKind = "EMAIL" | "SMS";

/** Practical email check: local@domain.tld (rejects values like "not-an-email"). */
export function isValidEmail(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed || trimmed.length > 254) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
}

/**
 * Phone must contain 10–15 digits after stripping formatting.
 * Rejects junk like "abc" or "+1".
 */
export function isValidPhone(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) return false;
  const digits = trimmed.replace(/\D/g, "");
  return digits.length >= 10 && digits.length <= 15;
}

/** Returns an error message, or null when the recipient is valid. */
export function validateRecipient(
  kind: RecipientKind,
  value: string,
): string | null {
  const trimmed = value.trim();
  if (!trimmed) {
    return kind === "EMAIL"
      ? "Enter an email address"
      : "Enter a phone number";
  }
  if (kind === "EMAIL") {
    return isValidEmail(trimmed) ? null : "Enter a valid email address";
  }
  return isValidPhone(trimmed)
    ? null
    : "Enter a valid phone number (at least 10 digits)";
}

type ContactChannelForValidation = {
  channelCode?: string | null;
  isRequired: boolean;
  destinationRuleJson?: string | null;
};

type ContactTierForValidation = {
  tierOrder: number;
  displayName?: string;
  channels: ContactChannelForValidation[];
};

/**
 * Validates EMAIL/SMS contacts on a policy draft before save.
 * Enabled email/text channels need at least one valid recipient;
 * "Must go through" channels cannot be empty.
 */
export function validatePolicyContacts(
  tiers: ContactTierForValidation[],
): string | null {
  for (const tier of tiers) {
    const stepLabel = `Step ${tier.tierOrder}`;
    for (const channel of tier.channels) {
      const code = (channel.channelCode ?? "").toUpperCase();
      if (code !== "EMAIL" && code !== "SMS") continue;

      const kind = code as RecipientKind;
      const label = kind === "EMAIL" ? "email" : "text message";
      const recipients = parseRecipients(channel.destinationRuleJson);

      if (recipients.length === 0) {
        if (channel.isRequired) {
          return `${stepLabel}: Must go through ${label} needs at least one contact`;
        }
        return `${stepLabel}: Add at least one ${label} contact, or turn the channel off`;
      }

      for (const recipient of recipients) {
        const error = validateRecipient(kind, recipient);
        if (error) {
          return `${stepLabel}: ${error} (“${recipient}”)`;
        }
      }
    }
  }
  return null;
}

export const CHANNEL_CODES = ["EMAIL", "SMS", "APP", "API"] as const;

export const ROLE_PRESETS = [
  { code: "PCT", label: "Patient Care Technician" },
  { code: "NURSE", label: "Nurse" },
  { code: "CHARGE_NURSE", label: "Charge Nurse" },
  { code: "HOUSE_SUPERVISOR", label: "House Supervisor" },
] as const;

export function buildEmergencyPolicyDraft(
  channels: CatalogNotificationChannel[],
): {
  code: string;
  displayName: string;
  isActive: boolean;
  tiers: Array<{
    escalationTierId?: number | null;
    tierOrder: number;
    roleCode: string;
    timeoutSeconds: number;
    displayName: string;
    channels: Array<{
      escalationTierChannelId?: number | null;
      notificationChannelId: number;
      sortOrder: number;
      isRequired: boolean;
      templateKey?: string | null;
      destinationRuleJson?: string | null;
    }>;
  }>;
} {
  const byCode = Object.fromEntries(
    channels.map((c) => [c.channelCode.toUpperCase(), c]),
  ) as Record<string, CatalogNotificationChannel>;

  const link = (code: string, sort: number, required: boolean) => {
    const ch = byCode[code];
    if (!ch) return null;
    return {
      notificationChannelId: ch.notificationChannelId,
      sortOrder: sort,
      isRequired: required,
      templateKey: `emergency_tier_${sort}_${code.toLowerCase()}`,
      destinationRuleJson: null as string | null,
    };
  };

  const t1 = [link("SMS", 1, true), link("APP", 2, false)].filter(Boolean);
  const t2 = [link("SMS", 1, true), link("API", 2, true)].filter(Boolean);

  return {
    code: "EMERGENCY",
    displayName: "Emergency — Nurse → Charge (fast)",
    isActive: true,
    tiers: [
      {
        tierOrder: 1,
        roleCode: "NURSE",
        timeoutSeconds: 30,
        displayName: "Nurse",
        channels: t1 as never[],
      },
      {
        tierOrder: 2,
        roleCode: "CHARGE_NURSE",
        timeoutSeconds: 30,
        displayName: "Charge Nurse",
        channels: t2 as never[],
      },
    ],
  };
}
